import { Button } from 'shared-ui';

import { useLocale } from 'hooks/useLocale/useLocale';
import { AppMessages } from 'i18n/messages';
import * as DetailsStyled from '../components/detailsItem/DetailsItem.styles';
import { StateChip } from '../components/stateChip/StateChip';
import { dodCommandLabel } from '../components/dodCommands';

import * as Styled from './DlqTab.styles';
import { DlqRow, dlqErrorClassState, dlqPayloadText } from './dlq.helpers';

export type DlqDetailsProps = {
  row: DlqRow;
  canModify: boolean;
  isMutating: boolean;
  onRetry: (id: string) => void;
  onPurge: (id: string) => void;
  onClose: () => void;
};

/**
 * Body of the dead-letter details panel, published by the container into the shell's
 * `renderDetails` slot so it sits beside the table like every other list-and-details page.
 *
 * A separate component so it can be tested on its own: the shared-ui `DetailsDrawer` is a stub in
 * the frontend's jest mock, and content behind it would otherwise go unverified.
 */
export const DlqDetails = ({ row, canModify, isMutating, onRetry, onPurge, onClose }: DlqDetailsProps): JSX.Element => {
  const { formatMessage } = useLocale();

  const unknownLabel = formatMessage({ id: AppMessages['dod.overview.unknown'] });

  return (
    <div data-testid="dod-dlq-details">
      <DetailsStyled.DetailsItemWrapper>
        <DetailsStyled.DetailName>
          {formatMessage({ id: AppMessages['dod.dlq.details.time'] })}
        </DetailsStyled.DetailName>
        <DetailsStyled.DetailText>{new Date(row.timestamp).toLocaleString()}</DetailsStyled.DetailText>
      </DetailsStyled.DetailsItemWrapper>

      <DetailsStyled.DetailsItemWrapper>
        <DetailsStyled.DetailName>
          {formatMessage({ id: AppMessages['dod.dlq.details.rcpTkd'] })}
        </DetailsStyled.DetailName>
        <DetailsStyled.DetailText>
          {row.rcpLabel} · {row.tkdLabel}
        </DetailsStyled.DetailText>
      </DetailsStyled.DetailsItemWrapper>

      <DetailsStyled.DetailsItemWrapper>
        <DetailsStyled.DetailName>
          {formatMessage({ id: AppMessages['dod.dlq.details.command'] })}
        </DetailsStyled.DetailName>
        <DetailsStyled.DetailText>
          {dodCommandLabel(String(row.numerRozkazu).padStart(2, '0'))}
        </DetailsStyled.DetailText>
      </DetailsStyled.DetailsItemWrapper>

      <DetailsStyled.DetailsItemWrapper>
        <DetailsStyled.DetailName>{formatMessage({ id: AppMessages['dod.dlq.details.kad'] })}</DetailsStyled.DetailName>
        <DetailsStyled.DetailText>{row.kad}</DetailsStyled.DetailText>
      </DetailsStyled.DetailsItemWrapper>

      <DetailsStyled.DetailsItemWrapper>
        <DetailsStyled.DetailName>
          {formatMessage({ id: AppMessages['dod.dlq.details.attempts'] })}
        </DetailsStyled.DetailName>
        <DetailsStyled.DetailText>
          {row.retryCount}/{row.maxRetries}
        </DetailsStyled.DetailText>
      </DetailsStyled.DetailsItemWrapper>

      <DetailsStyled.DetailsItemWrapper>
        <DetailsStyled.DetailName>
          {formatMessage({ id: AppMessages['dod.dlq.details.reason'] })}
        </DetailsStyled.DetailName>
        <DetailsStyled.DetailText>
          <StateChip state={row.reason} />
        </DetailsStyled.DetailText>
      </DetailsStyled.DetailsItemWrapper>

      <DetailsStyled.DetailsItemWrapper>
        <DetailsStyled.DetailName>
          {formatMessage({ id: AppMessages['dod.dlq.details.errorClass'] })}
        </DetailsStyled.DetailName>
        <DetailsStyled.DetailText>
          <StateChip state={dlqErrorClassState(row.errorClass)} />
        </DetailsStyled.DetailText>
      </DetailsStyled.DetailsItemWrapper>

      <DetailsStyled.DetailsItemWrapper>
        <DetailsStyled.DetailName>
          {formatMessage({ id: AppMessages['dod.dlq.details.errorCode'] })}
        </DetailsStyled.DetailName>
        <DetailsStyled.DetailTextBold>
          {row.errorCode === undefined ? unknownLabel : `${row.errorCode}${row.errorName ? ` ${row.errorName}` : ''}`}
        </DetailsStyled.DetailTextBold>
      </DetailsStyled.DetailsItemWrapper>

      <DetailsStyled.DetailsItemWrapper>
        <DetailsStyled.DetailName>
          {formatMessage({ id: AppMessages['dod.dlq.details.lastError'] })}
        </DetailsStyled.DetailName>
        <DetailsStyled.DetailText>{row.lastError || unknownLabel}</DetailsStyled.DetailText>
      </DetailsStyled.DetailsItemWrapper>

      <DetailsStyled.DetailsItemWrapper>
        <DetailsStyled.DetailName>
          {formatMessage({ id: AppMessages['dod.dlq.details.payload'] })}
        </DetailsStyled.DetailName>
        <Styled.PayloadBlock>{dlqPayloadText(row)}</Styled.PayloadBlock>
      </DetailsStyled.DetailsItemWrapper>

      {/* Two small buttons per row: the panel is 400 px wide and three medium ones do not fit. */}
      {canModify && (
        <DetailsStyled.ButtonsWrapper>
          <Button size="small" variant="contained" disabled={isMutating} onClick={() => onRetry(row.id)}>
            {formatMessage({ id: AppMessages['dod.dlq.retry'] })}
          </Button>
          <Button size="small" variant="outlined" disabled={isMutating} onClick={() => onPurge(row.id)}>
            {formatMessage({ id: AppMessages['dod.dlq.purge'] })}
          </Button>
        </DetailsStyled.ButtonsWrapper>
      )}
      <DetailsStyled.ButtonsWrapper>
        <Button size="small" variant="outlined" onClick={onClose}>
          {formatMessage({ id: AppMessages['common.close'] })}
        </Button>
      </DetailsStyled.ButtonsWrapper>
    </div>
  );
};
